import React from 'react';
import { Sparkles } from 'lucide-react';

const SET_LABEL = {
  artist: {
    ru: 'Один автор', en: 'Same artist', es: 'Mismo artista', ar: 'نفس الفنان',
    uk: 'Один автор', fr: 'Même artiste', pt: 'Mesmo artista',
  },
  year: {
    ru: 'Один год', en: 'Same year', es: 'Mismo año', ar: 'نفس السنة',
    uk: 'Один рік', fr: 'Même année', pt: 'Mesmo ano',
  },
  rarity: {
    ru: 'Одна редкость', en: 'Same rarity', es: 'Misma rareza', ar: 'نفس الندرة',
    uk: 'Одна рідкість', fr: 'Même rareté', pt: 'Mesma raridade',
  },
  unique: {
    ru: 'Уникальные', en: 'Unique', es: 'Unique', ar: 'Unique',
    uk: 'Унікальні', fr: 'Unique', pt: 'Unique',
  },
  ton: { ru: 'TON', en: 'TON', es: 'TON', ar: 'TON', uk: 'TON', fr: 'TON', pt: 'TON' },
};

const FLAG_COLORS = { unique: '#F5D07A', ton: '#0098EA' };

const RARITY_NAMES = {
  common: { en: 'Common', ru: 'Обычная', es: 'Común', ar: 'عادية', uk: 'Звичайна', fr: 'Commune', pt: 'Comum' },
  uncommon: { en: 'Uncommon', ru: 'Необычная', es: 'Poco común', ar: 'غير شائعة', uk: 'Незвичайна', fr: 'Peu commune', pt: 'Incomum' },
  rare: { en: 'Rare', ru: 'Редкая', es: 'Rara', ar: 'نادرة', uk: 'Рідкісна', fr: 'Rare', pt: 'Rara' },
  legendary: { en: 'Legendary', ru: 'Легендарная', es: 'Legendaria', ar: 'أسطورية', uk: 'Легендарна', fr: 'Légendaire', pt: 'Lendária' },
  masterpiece: { en: 'Masterpiece', ru: 'Шедевр', es: 'Obra maestra', ar: 'تحفة', uk: 'Шедевр', fr: 'Chef-d’œuvre', pt: 'Obra-prima' },
};

const RARITY_COLORS = {
  common: '#9CA3AF', uncommon: '#10B981', rare: '#3B82F6',
  legendary: '#A855F7', masterpiece: '#F59E0B',
};

// Each painting earns the set rate scaled by its own rarity, so a mixed
// set pays a range. The colour follows the rarity that earns the top
// rate — a rarity set needs no extra note, its name already is one.
const Rate = ({ set }) => {
  const color = FLAG_COLORS[set.type] || RARITY_COLORS[set.rarity] || '#10B981';
  const spread = set.min_percent != null && set.min_percent !== set.percent;
  return (
    <b style={{ color }}>
      {spread && <span style={{ opacity: 0.65 }}>+{set.min_percent}…</span>}
      {spread ? `${set.percent}%` : `+${set.percent}%`}
    </b>
  );
};

// Badges for the qualifying sets inside a collection.
export const SetBadges = ({ sets, language, limit = 5 }) => {
  if (!sets?.length) return null;
  const key = SET_LABEL.artist[language] ? language : 'en';
  // Unique / TON pay a small flat percent, so sorting by percent buries
  // them behind the attribute sets and a tight `limit` (the Top list
  // shows two) cut them off entirely. They are the badges players look
  // for, so they always get a slot.
  const flags = sets.filter((s) => FLAG_COLORS[s.type]);
  const rest = sets.filter((s) => !FLAG_COLORS[s.type]);
  const shown = [...rest.slice(0, Math.max(1, limit - flags.length)), ...flags];
  return (
    <div className="flex flex-wrap gap-1" data-testid="collection-set-badges">
      {shown.map((s, i) => (
        <span
          key={`${s.type}-${s.key}-${i}`}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-medium"
          style={{
            background: 'linear-gradient(135deg, rgba(212,175,55,0.22), rgba(245,208,122,0.12))',
            border: '1px solid rgba(212,175,55,0.45)',
            color: '#F5D07A',
          }}
          data-testid={`collection-set-badge-${s.type}`}
        >
          <Sparkles className="w-2.5 h-2.5" />
          {SET_LABEL[s.type]?.[key] || s.type}
          {!FLAG_COLORS[s.type] &&
            `: ${s.type === 'rarity' ? (RARITY_NAMES[s.key]?.[key] || s.key) : s.key}`} ×{s.size}
          <Rate set={s} />
        </span>
      ))}
    </div>
  );
};

export default SetBadges;
